import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components';
import axios from 'axios';
import { 
  FiArrowLeft, FiPlus, FiEdit, FiTrash2, FiCheck, 
  FiX, FiBook, FiClock, FiCalendar, FiLoader, FiAlertCircle 
} from 'react-icons/fi';
import { FaFolder, FaFileAlt, FaArrowLeft as FaArrowLeftIcon } from 'react-icons/fa';
import { useStateContext } from '../contexts/ContextProvider';

const TestSeriesContent = () => {
  // Make sure contentId is properly received - using different approach
  const params = useParams();
  const location = window.location;
  
  // Extract content ID from URL if params don't work
  const contentIdFromURL = location.pathname.split('/').pop();
  
  // Get the testId from params, which is our contentId
  const effectiveContentId = params.testId || contentIdFromURL;
  
  // For debugging
  useEffect(() => {
    console.log("Params object:", params);
    console.log("Test ID from params:", params.testId);
    console.log("Current URL:", location.pathname);
    console.log("ID extracted from URL:", contentIdFromURL);
    console.log("Effective content ID:", effectiveContentId);
  }, [params, location.pathname, contentIdFromURL, effectiveContentId]);
  const { currentColor } = useStateContext();
  const navigate = useNavigate();
  
  const [testSeries, setTestSeries] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [availableTests, setAvailableTests] = useState([]);
  const   [loadingAvailableTests, setLoadingAvailableTests] = useState(false);
  const fetchTestDetails = async (testId) => {
    try {
      console.log(`Fetching test details for ID: ${testId}`);
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/Tests/${testId}`);
      console.log('Test details response:', response.data);
      return response.data;
    } catch (err) {
      console.error(`Error fetching test details for ID ${testId}:`, err);
      return null;
    }
  };
  
  // Load test series data and its tests
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (!effectiveContentId) {
          console.error("Content ID is undefined");
          setError('Missing content ID. Please go back and try again.');
          setLoading(false);
          return;
        }
        
        console.log("Fetching data for content ID:", effectiveContentId);
        
        // First, fetch the test series details
        const seriesResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/Contents/${effectiveContentId}`);
        setTestSeries(seriesResponse.data);
        
        // Then fetch all tests for this test series using the dedicated endpoint
        try {
          const testsResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/test-series-tests/by-series/${effectiveContentId}`);
          setTests(testsResponse.data);
          
          // If no tests exist, fetch available tests that can be added
          if (!testsResponse.data || testsResponse.data.length === 0) {
            fetchAvailableTests();
          }
        } catch (err) {
          console.error('Error fetching tests:', err);
          // If the endpoint fails (e.g., 404 if no tests exist yet), set empty array
          setTests([]);
          // Fetch available tests that can be added
          fetchAvailableTests();
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load test series content. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [effectiveContentId]);
  
  // Fetch available tests for TEST_SERIES type
  const fetchAvailableTests = async () => {
    try {
      setLoadingAvailableTests(true);
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/test-type-assignments/type/TEST_SERIES');
      
      // Fetch test details for each available test
      const testsWithDetails = await Promise.all(
        response.data.map(async (test) => {
          const testDetails = await fetchTestDetails(test.test_id);
          return {
            ...test,
            testDetails
          };
        })
      );
      
      setAvailableTests(testsWithDetails);
    } catch (err) {
      console.error('Error fetching available tests:', err);
      setAvailableTests([]);
    } finally {
      setLoadingAvailableTests(false);
    }
  };
  
  // Handle adding a new test
  const handleAddTest = async (formData) => {
    try {
      const response = await axios.post('https://apissc.sainikschoolcadet.com/api/test-series-tests', {
        test_series_id: parseInt(effectiveContentId),
        test_id: formData.testId,
        title: formData.title,
        start_time: formData.start_time ? new Date(formData.start_time).toISOString() : null,
        end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null
      });
      
      // Add the new test to the list
      setTests(prevTests => [...prevTests, response.data]);
      setShowAddForm(false);
      
      return { success: true };
    } catch (err) {
      console.error('Error adding test:', err);
      return { 
        success: false, 
        error: err.response?.data?.details || err.response?.data?.error || 'Failed to add test. Please try again.' 
      };
    }
  };
  
  // Handle editing a test
  const handleEditTest = async (formData) => {
    try {
      const response = await axios.put(`https://apissc.sainikschoolcadet.com/api/test-series-tests/${currentTest.id}`, {
        test_series_id: parseInt(effectiveContentId),
        test_id: formData.testId,
        title: formData.title,
        start_time: formData.start_time ? new Date(formData.start_time).toISOString() : null,
        end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null
      });
      
      // Update the test in the list
      setTests(prevTests => 
        prevTests.map(test => test.id === currentTest.id ? response.data : test)
      );
      setShowEditForm(false);
      setCurrentTest(null);
      
      return { success: true };
    } catch (err) {
      console.error('Error updating test:', err);
      return { 
        success: false, 
        error: err.response?.data?.details || err.response?.data?.error || 'Failed to update test. Please try again.' 
      };
    }
  };
  
  // Handle deleting a test
  const handleDeleteTest = async (id) => {
    try {
      await axios.delete(`https://apissc.sainikschoolcadet.com/api/test-series-tests/${id}`);
      
      // Remove the test from the list
      setTests(prevTests => prevTests.filter(test => test.id !== id));
      setConfirmDelete(null);
      
      return { success: true };
    } catch (err) {
      console.error('Error deleting test:', err);
      return { 
        success: false, 
        error: 'Failed to delete test. Please try again.' 
      };
    }
  };
  
  // Helper to format ISO to datetime-local input
  const formatForDateTimeInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  // Test Form Component (used for both Add and Edit)
  const TestForm = ({ onSubmit, initialData, title, onCancel }) => {
    const [formData, setFormData] = useState({
      testId: initialData?.test_id || '',
      title: initialData?.title || '',
      start_time: formatForDateTimeInput(initialData?.start_time),
      end_time: formatForDateTimeInput(initialData?.end_time)
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [selectedAvailableTest, setSelectedAvailableTest] = useState(null);
    const [testDetails, setTestDetails] = useState(null);
    const [loadingTestDetails, setLoadingTestDetails] = useState(false);
    const [useOriginalName, setUseOriginalName] = useState(true);
    
    // Folder/Test browser states
    const [showTestBrowser, setShowTestBrowser] = useState(false);
    const [folders, setFolders] = useState([]);
    const [tests, setTests] = useState([]);
    const [parentStack, setParentStack] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [loadingFolders, setLoadingFolders] = useState(false);
    const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';
    
    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // If test ID changes, fetch test details
      if (name === 'testId' && value) {
        fetchTestDetailsForForm(value);
      }
    };
    
    // Fetch test details when test ID changes
    const fetchTestDetailsForForm = async (testId) => {
      setLoadingTestDetails(true);
      try {
        const details = await fetchTestDetails(testId);
        setTestDetails(details);
        
        // Auto-fill title if it's empty or was previously auto-filled
        if (useOriginalName && details && (!formData.title || formData.title === `Test Series Test ${testId}`)) {
          setFormData(prev => ({ 
            ...prev, 
            title: details.name || `Test Series Test ${testId}`
          }));
        }
      } catch (err) {
        console.error('Error fetching test details:', err);
      } finally {
        setLoadingTestDetails(false);
      }
    };
    
    const handleSelectAvailableTest = (test) => {
      setSelectedAvailableTest(test);
      setTestDetails(test.testDetails);
      setUseOriginalName(true);
      
      setFormData(prev => ({ 
        ...prev, 
        testId: test.test_id,
        // Use test name if available, otherwise default
        title: test.testDetails?.name || `Test Series Test ${test.test_id}`
      }));
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setFormError(null);
      
      // Validate that a test is selected when adding
      if (!initialData && !formData.testId) {
        setFormError('Please select a test before submitting.');
        setSubmitting(false);
        return;
      }

      if (formData.start_time && formData.end_time) {
        const start = new Date(formData.start_time);
        const end = new Date(formData.end_time);
        if (end <= start) {
          setFormError('End time must be after start time.');
          setSubmitting(false);
          return;
        }
      }
      
      const result = await onSubmit(formData);
      
      if (!result.success) {
        setFormError(result.error);
        setSubmitting(false);
      }
    };
    
    // Toggle between original test name and custom name
    const toggleNameUse = () => {
      setUseOriginalName(!useOriginalName);
      if (!useOriginalName && testDetails) {
        // Switch back to original name
        setFormData(prev => ({
          ...prev,
          title: testDetails.name || `Test Series Test ${formData.testId}`
        }));
      }
    };
    
    // Fetch test details if testId is provided in initialData
    useEffect(() => {
      if (initialData?.test_id) {
        fetchTestDetailsForForm(initialData.test_id);
      }
    }, [initialData]);
    
    // Fetch root folders for test browser
    const fetchRootFolders = async () => {
      setLoadingFolders(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/Folders/root`);
        setFolders(response.data.data || []);
        setCurrentFolder(null);
        setParentStack([]);
        
        // Also fetch root tests
        const testsResponse = await axios.get(`${API_BASE_URL}/Tests`);
        setTests(testsResponse.data.data || []);
      } catch (err) {
        console.error('Error fetching root folders:', err);
        setFormError('Error loading folders: ' + err.message);
      } finally {
        setLoadingFolders(false);
      }
    };
    
    // Fetch folder content (both subfolders and tests)
    const fetchFolderContent = async (folderId) => {
      setLoadingFolders(true);
      try {
        if (folderId !== null) {
          const folderResponse = await axios.get(`${API_BASE_URL}/Folders/parent/${folderId}`);
          setFolders(folderResponse.data.data || []);
        }
        
        const testsUrl = folderId === null 
          ? `${API_BASE_URL}/Tests` 
          : `${API_BASE_URL}/Tests/folder/${folderId}`;
        
        const testsResponse = await axios.get(testsUrl);
        setTests(testsResponse.data.data || []);
        setCurrentFolder(folderId);
      } catch (err) {
        console.error('Error fetching folder content:', err);
        setFormError('Error loading folder content: ' + err.message);
      } finally {
        setLoadingFolders(false);
      }
    };
    
    // Navigate to a specific folder
    const navigateToFolder = (folderId, folderName) => {
      if (currentFolder !== null) {
        setParentStack([...parentStack, { id: currentFolder, name: getCurrentFolderName() }]);
      }
      fetchFolderContent(folderId);
    };
    
    // Get current folder name
    const getCurrentFolderName = () => {
      if (currentFolder === null) return 'Root';
      const folder = folders.find(f => f.id === currentFolder);
      return folder ? folder.name : 'Folder';
    };
    
    // Navigate back to parent folder
    const navigateBack = () => {
      if (parentStack.length === 0) {
        fetchRootFolders();
        return;
      }
      const newStack = [...parentStack];
      const prevFolder = newStack.pop();
      setParentStack(newStack);
      fetchFolderContent(prevFolder.id);
    };
    
    // Select a test from browser
    const selectTestFromBrowser = async (test) => {
      setSelectedAvailableTest(test);
      const details = await fetchTestDetails(test.test_id);
      setTestDetails(details);
      setUseOriginalName(true);
      
      setFormData(prev => ({ 
        ...prev, 
        testId: test.test_id,
        title: test.name || `Test Series Test ${test.test_id}`
      }));
      
      setShowTestBrowser(false);
    };
    
    // Open test browser
    const openTestBrowser = () => {
      setShowTestBrowser(true);
      fetchRootFolders();
    };
    
    return (
      <>
        {/* Test Browser Modal */}
        {showTestBrowser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[85vh] flex flex-col shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Select Test</h3>
                <button 
                  type="button" 
                  onClick={() => setShowTestBrowser(false)} 
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <FiX />
                </button>
              </div>
              
              {/* Breadcrumb Navigation */}
              <div className="mb-4 flex items-center">
                {(parentStack.length > 0 || currentFolder !== null) && (
                  <button 
                    onClick={navigateBack}
                    className="mr-3 p-1 rounded hover:bg-gray-100"
                  >
                    <FaArrowLeftIcon />
                  </button>
                )}
                <div className="flex items-center text-sm text-gray-600 overflow-x-auto">
                  <button 
                    onClick={() => {
                      setParentStack([]);
                      fetchRootFolders();
                    }}
                    className="hover:text-blue-600 whitespace-nowrap"
                  >
                    Root
                  </button>
                  {parentStack.map((folder, index) => (
                    <React.Fragment key={folder.id}>
                      <span className="mx-1">/</span>
                      <button 
                        onClick={() => {
                          const newStack = parentStack.slice(0, index);
                          setParentStack(newStack);
                          fetchFolderContent(folder.id);
                        }}
                        className="hover:text-blue-600 whitespace-nowrap"
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                  {currentFolder !== null && (
                    <>
                      <span className="mx-1">/</span>
                      <span className="font-medium whitespace-nowrap">{getCurrentFolderName()}</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Test and Folder Browser Content */}
              <div className="flex-1 overflow-y-auto mb-4 border rounded-lg p-4 bg-gray-50">
                {loadingFolders ? (
                  <div className="flex items-center justify-center h-48">
                    <FiLoader className="animate-spin mr-2" /> Loading content...
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Folders List */}
                    {folders.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Folders</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {folders.map(folder => (
                            <div 
                              key={folder.id}
                              onClick={() => navigateToFolder(folder.id, folder.name)}
                              className="p-3 bg-white border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50 cursor-pointer flex items-center transition-colors"
                            >
                              <FaFolder className="text-yellow-500 mr-2 flex-shrink-0" />
                              <span className="truncate text-sm font-medium">{folder.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Tests List */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tests</h4>
                      {tests.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 text-sm">
                          No tests found in this folder
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {tests.map(test => (
                            <div 
                              key={test.test_id}
                              onClick={() => selectTestFromBrowser(test)}
                              className="p-3 bg-white border border-gray-200 rounded-md hover:border-blue-500 hover:bg-blue-50 cursor-pointer flex items-center justify-between transition-colors"
                            >
                              <div className="flex items-center space-x-2 truncate">
                                <FaFileAlt className="text-blue-500 flex-shrink-0" />
                                <div className="truncate">
                                  <div className="text-sm font-medium truncate">{test.name}</div>
                                  <div className="text-xs text-gray-500">
                                    Duration: {test.time_duration} mins
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end pt-3 border-t">
                <button
                  onClick={() => setShowTestBrowser(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Main Form Modal */}
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">{title}</h2>
              <button 
                type="button" 
                onClick={onCancel} 
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <FiX />
              </button>
            </div>
            
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
                {formError}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Test Selection - Only show browser for adding new tests */}
                {!initialData && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Test Name <span className="text-red-500">*</span>
                    </label>
                    {formData.testId ? (
                      <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {testDetails?.name || `Test ID: ${formData.testId}`}
                            </div>
                            {testDetails && (
                              <div className="text-xs text-gray-500 mt-1">
                                Duration: {testDetails.time_duration} min | ID: {formData.testId}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={openTestBrowser}
                            className="ml-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50"
                          >
                            Change
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={openTestBrowser}
                        className="w-full p-3 border-2 border-dashed border-gray-300 rounded-md hover:border-blue-400 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                      >
                        <FaFileAlt className="text-blue-500" />
                        <span>Click to browse and select a test</span>
                      </button>
                    )}
                    {loadingTestDetails && (
                      <div className="text-xs text-blue-500 flex items-center mt-1">
                        <FiLoader className="animate-spin mr-1" /> Loading test details...
                      </div>
                    )}
                  </div>
                )}
                
                {/* Test ID field - Only for editing */}
                {initialData && (
                  <div>
                    <label htmlFor="testId" className="block text-sm font-medium text-gray-700 mb-1">
                      Test ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="testId"
                      name="testId"
                      value={formData.testId}
                      onChange={handleChange}
                      required
                      min="1"
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {loadingTestDetails ? (
                      <div className="text-xs text-blue-500 flex items-center mt-1">
                        <FiLoader className="animate-spin mr-1" /> Loading test details...
                      </div>
                    ) : testDetails ? (
                      <div className="text-xs text-green-600 mt-1 flex items-center">
                        <FiCheck className="mr-1" /> Found: {testDetails.name || 'Unnamed Test'} ({testDetails.time_duration} min)
                      </div>
                    ) : formData.testId ? (
                      <div className="text-xs text-orange-500 mt-1">
                        No test details found for this ID
                      </div>
                    ) : null}
                  </div>
                )}
              
              {/* Test Title field with option to use original name */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="title" className="text-sm font-medium text-gray-700">
                    Title <span className="text-red-500">*</span>
                  </label>
                  
                  {testDetails && (
                    <div className="flex items-center text-sm">
                      <label htmlFor="useOriginalName" className="mr-2 text-gray-700">
                        Use original name
                      </label>
                      <div
                        id="useOriginalName"
                        onClick={toggleNameUse}
                        className={`relative w-10 h-5 rounded-full cursor-pointer transition-colors duration-200 ${
                          useOriginalName ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                            useOriginalName ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, title: e.target.value }));
                    setUseOriginalName(false);
                  }}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Mock Test 1"
                  disabled={useOriginalName && testDetails}
                />
                
                {testDetails && (
                  <p className="text-xs text-gray-500 mt-1">
                    {useOriginalName 
                      ? "Using the original test name"
                      : "Custom title for this test series"}
                  </p>
                )}
              </div>

              {/* Start and End Time scheduling */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="start_time" className="block text-xs font-medium text-gray-700 mb-1">
                    Start Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    id="start_time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="end_time" className="block text-xs font-medium text-gray-700 mb-1">
                    End Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    id="end_time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              {/* Test Details Summary (if found) */}
              {testDetails && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Test Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="text-gray-600">Test ID:</div>
                    <div className="font-medium">{testDetails.test_id}</div>
                    
                    <div className="text-gray-600">Name:</div>
                    <div className="font-medium">{testDetails.name || 'N/A'}</div>
                    
                    <div className="text-gray-600">Duration:</div>
                    <div className="font-medium">{testDetails.time_duration} minutes</div>
                    
                    <div className="text-gray-600">Status:</div>
                    <div className="font-medium">
                      {testDetails.publish_status 
                        ? <span className="text-green-600">Published</span> 
                        : <span className="text-orange-500">Unpublished</span>}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 mr-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white rounded-md"
                  style={{ backgroundColor: currentColor }}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      </>
    );
  };
  
  // Delete Confirmation Dialog
  const DeleteConfirmDialog = ({ test, onCancel, onConfirm }) => {
    const [deleting, setDeleting] = useState(false);
    
    const handleConfirm = async () => {
      setDeleting(true);
      await onConfirm(test.id);
      setDeleting(false);
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
        <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center mx-auto mb-4">
              <FiTrash2 className="text-xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove Test</h3>
            <p className="text-sm text-gray-500">
              Are you sure you want to remove <span className="font-medium text-gray-900">{test.title}</span> from this test series?
            </p>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? 'Removing...' : 'Remove'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Test Card Component
  const TestCard = ({ test }) => {
    const [testDetails, setTestDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch test details
    useEffect(() => {
      const loadDetails = async () => {
        try {
          const details = await fetchTestDetails(test.test_id);
          setTestDetails(details);
        } catch (err) {
          console.error(`Error loading details for test ${test.test_id}:`, err);
        } finally {
          setLoading(false);
        }
      };
      
      loadDetails();
    }, [test.test_id]);

    const getStatusBadge = () => {
      if (!test.start_time && !test.end_time) {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
            Unrestricted
          </span>
        );
      }

      const now = new Date();
      const start = test.start_time ? new Date(test.start_time) : null;
      const end = test.end_time ? new Date(test.end_time) : null;

      if (start && now < start) {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            Upcoming
          </span>
        );
      } else if (end && now >= end) {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            Ended
          </span>
        );
      } else {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        );
      }
    };

    return (
      <div className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col justify-between">
        <div>
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-lg text-gray-900 mb-1">
              {loading ? (
                <div className="h-6 w-3/4 bg-gray-200 rounded animate-pulse"></div>
              ) : (
                test.title || testDetails?.name
              )}
            </h3>
            <div className="flex items-center space-x-1">
              <button 
                onClick={() => {
                  setCurrentTest(test);
                  setShowEditForm(true);
                }}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                title="Edit Test"
              >
                <FiEdit />
              </button>
              <button 
                onClick={() => setConfirmDelete(test)}
                className="p-1.5 text-gray-500 hover:bg-red-100 hover:text-red-500 rounded-full transition-colors"
                title="Delete Test"
              >
                <FiTrash2 />
              </button>
            </div>
          </div>
          <div className="mt-1 mb-2">
            {getStatusBadge()}
          </div>
        </div>
        
        <div className="mt-3 text-sm text-gray-500">
          <div className="flex items-center mb-1.5">
            <FiBook className="mr-2" />
            <span>Test ID: {test.test_id}</span>
          </div>
          {!loading && testDetails && (
            <div className="flex items-center mb-1.5">
              <FiClock className="mr-2" />
              <span>Duration: {testDetails.time_duration} minutes</span>
            </div>
          )}
          <div className="flex items-center">
            <FiCalendar className="mr-2" />
            <span>Added: {new Date(test.created_at).toLocaleDateString()}</span>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t">
          <a
            href={`/tests/${test.test_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full px-4 py-2 text-sm font-medium text-white rounded-md inline-block text-center"
            style={{ backgroundColor: currentColor }}
          >
            View Test
          </a>
        </div>
      </div>
    );
  };
  // Loading state
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <button 
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
            >
              <FiArrowLeft />
            </button>
            <div>
              <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-1"></div>
              <div className="h-4 w-24 bg-gray-100 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white border rounded-lg shadow-sm p-5">
              <div className="h-6 w-3/4 bg-gray-200 rounded animate-pulse mb-4"></div>
              <div className="h-4 w-1/2 bg-gray-100 rounded animate-pulse mb-2"></div>
              <div className="h-4 w-1/3 bg-gray-100 rounded animate-pulse mb-4"></div>
              <div className="mt-4 pt-4 border-t">
                <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <div className="flex items-center mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="mr-4 p-2 rounded-full hover:bg-gray-100"
          >
            <FiArrowLeft />
          </button>
          <Header category="Content" title="Test Series Content" />
        </div>
        
        <div className="p-6 bg-red-50 border border-red-200 rounded-md text-center">
          <FiAlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Content</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {/* Header with back button */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="mr-4 p-2 rounded-full hover:bg-gray-100"
            title="Go Back"
          >
            <FiArrowLeft />
          </button>
          <div>
            <h1 className="text-xl font-bold">{testSeries?.title || 'Test Series Content'}</h1>
            <p className="text-gray-500">Manage tests for this test series</p>
          </div>
        </div>
        
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-2"
          style={{ backgroundColor: currentColor }}
        >
          <FiPlus /> Add New Test
        </button>
      </div>
      
      {/* No tests message with more information about available tests */}
      {tests.length === 0 ? (
  <div className="bg-gray-50 rounded-lg p-8 text-center">
    <div className="flex justify-center mb-4">
      <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
        <FiBook className="text-gray-500 text-2xl" />
      </div>
    </div>
    <h3 className="text-lg font-medium text-gray-800 mb-2">No Tests Available</h3>
    <p className="text-gray-600 mb-6">
      This test series doesn't have any tests yet. Add your first test to get started.
    </p>

    <button
      onClick={() => setShowAddForm(true)}
      className="px-4 py-2 rounded-md text-white text-sm inline-flex items-center gap-2"
      style={{ backgroundColor: currentColor }}
    >
      <FiPlus /> Add First Test
    </button>
  </div>
) : (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {tests.map(test => (
      <TestCard key={test.id} test={test} />
    ))}
  </div>
)}

      {/* Add Test Form Dialog */}
      {showAddForm && (
        <TestForm
          title="Add New Test"
          onSubmit={handleAddTest}
          onCancel={() => setShowAddForm(false)}
        />
      )}
      
      {/* Edit Test Form Dialog */}
      {showEditForm && currentTest && (
        <TestForm
          title="Edit Test"
          initialData={currentTest}
          onSubmit={handleEditTest}
          onCancel={() => {
            setShowEditForm(false);
            setCurrentTest(null);
          }}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <DeleteConfirmDialog
          test={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleDeleteTest}
        />
      )}
    </div>
  );
};

export default TestSeriesContent;